import React, { useState, useEffect } from 'react';

const DescriptionBox = ({ selection, textColor }) => {
  const [musicData, setMusicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cachedXmlDoc, setCachedXmlDoc] = useState(null);

  // Fetch and cache XML data once on component mount
  useEffect(() => {
    const fetchXMLData = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/data/music-theory-data.xml`);
        if (!response.ok) {
          throw new Error('Failed to load music theory data');
        }
        
        const xmlText = await response.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
        
        // Check for parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error('Error parsing XML data');
        }
        
        setCachedXmlDoc(xmlDoc);
      } catch (err) {
        console.error('Error loading music theory data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchXMLData();
  }, []);

  // Find the matching item when selection changes
  useEffect(() => {
    if (!cachedXmlDoc) return;

    try {
      setLoading(true);
      setError(null);
      
      // Find the matching item based on selection using Array.find()
      const items = Array.from(cachedXmlDoc.querySelectorAll('item'));
      const matchedItem = items.find(item => item.getAttribute('id') === selection);
      
      if (matchedItem) {
        const foundItem = {
          name: matchedItem.querySelector('name')?.textContent || '',
          formula: matchedItem.querySelector('formula')?.textContent || '',
          description: matchedItem.querySelector('description')?.textContent || '',
          usage: matchedItem.querySelector('usage')?.textContent || '',
          examples: matchedItem.querySelector('examples')?.textContent || ''
        };
        setMusicData(foundItem);
      } else {
        setMusicData(null);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error processing music theory data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [selection, cachedXmlDoc]);

  const containerStyle = {
    width: '1080px',
    minHeight: '150px',
    border: `2px solid ${textColor}`,
    borderRadius: '8px',
    backgroundColor: 'rgba(128, 128, 128, 0.2)',
    padding: '20px',
    marginTop: '20px',
    color: textColor,
    fontFamily: 'Arial, sans-serif',
    boxSizing: 'border-box'
  };

  const headerStyle = {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '15px',
    borderBottom: `1px solid ${textColor}`,
    paddingBottom: '10px'
  };

  const formulaStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#6eb6ff',
    margin: '10px 0',
    padding: '8px',
    backgroundColor: 'rgba(110, 182, 255, 0.15)',
    borderRadius: '4px',
    textAlign: 'center'
  };

  const sectionStyle = {
    margin: '15px 0'
  };

  const sectionTitleStyle = {
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '5px',
    color: textColor
  };

  const sectionContentStyle = {
    fontSize: '14px',
    lineHeight: '1.6',
    marginLeft: '10px'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading music theory information...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '20px', color: '#ff6b6b' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!musicData) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No information available for this selection.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>{musicData.name}</div>
      
      <div style={formulaStyle}>
        Formula: {musicData.formula}
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Description:</div>
        <div style={sectionContentStyle}>{musicData.description}</div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Usage:</div>
        <div style={sectionContentStyle}>{musicData.usage}</div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Examples:</div>
        <div style={sectionContentStyle}>{musicData.examples}</div>
      </div>
    </div>
  );
};

export default DescriptionBox;
